/**
 * Overlay Template Library
 *
 * Static preset configurations for common ad text overlay layouts.
 * All positions/sizes use fractions (0–1) relative to canvas dimensions
 * so templates scale correctly to any image size.
 *
 * Apply via applyTemplate() in TextOverlayEditor — fractions are converted
 * to display-space pixels once the canvas size is known.
 */

import { DEFAULT_EFFECT_PARAMS, type EffectId, type EffectParams } from './presets';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TemplateLayer {
  text: string;
  /** Text-box centre X as fraction of canvas width (0–1) */
  xCentreFrac: number;
  /** Text-box centre Y as fraction of canvas height (0–1) */
  yCentreFrac: number;
  /** Text-box width as fraction of canvas width (0–1) */
  widthFrac: number;
  /** Font size as fraction of canvas height (0–1) */
  fontSizeFrac: number;
  fontFamily: string;
  fill: string;
  fontStyle: string;
  align: 'left' | 'center' | 'right';
  opacity: number;
  rotation: number;
  effect: EffectId;
  effectParams: Partial<EffectParams>;
  /** Optional background shape behind the text */
  bgShape?: 'none' | 'rect' | 'pill';
  bgColor?: string;
  bgOpacity?: number;
  /** Letter spacing in logical pixels (pre-scale) */
  letterSpacing?: number;
}

export interface OverlayTemplate {
  id: string;
  name: string;
  category: 'promotional' | 'minimal' | 'bold' | 'elegant';
  emoji: string;
  description: string;
  layers: TemplateLayer[];
}

// ─── Template definitions ─────────────────────────────────────────────────────

export const OVERLAY_TEMPLATES: OverlayTemplate[] = [
  // ── 1. Centered Bold ─────────────────────────────────────────────────────
  {
    id: 'centered_bold',
    name: 'Centered Bold',
    category: 'bold',
    emoji: '🎯',
    description: 'Single strong headline centred on the image',
    layers: [
      {
        text: 'YOUR HEADLINE',
        xCentreFrac: 0.50, yCentreFrac: 0.50,
        widthFrac: 0.82, fontSizeFrac: 0.10,
        fontFamily: 'Impact', fill: '#ffffff', fontStyle: 'bold',
        align: 'center', opacity: 1, rotation: 0,
        effect: 'none', effectParams: {},
      },
    ],
  },

  // ── 2. Bottom Banner ─────────────────────────────────────────────────────
  {
    id: 'bottom_banner',
    name: 'Bottom Banner',
    category: 'promotional',
    emoji: '📢',
    description: 'Title + tagline stacked at the bottom',
    layers: [
      {
        text: 'SUMMER SALE',
        xCentreFrac: 0.50, yCentreFrac: 0.80,
        widthFrac: 0.85, fontSizeFrac: 0.095,
        fontFamily: 'Montserrat', fill: '#ffffff', fontStyle: 'bold',
        align: 'center', opacity: 1, rotation: 0,
        effect: 'none', effectParams: {},
      },
      {
        text: 'Up to 50% off — Shop Now',
        xCentreFrac: 0.50, yCentreFrac: 0.91,
        widthFrac: 0.75, fontSizeFrac: 0.045,
        fontFamily: 'Open Sans', fill: '#ffffffcc', fontStyle: 'normal',
        align: 'center', opacity: 0.92, rotation: 0,
        effect: 'none', effectParams: {},
      },
    ],
  },

  // ── 3. Top Title + Bottom CTA ─────────────────────────────────────────────
  {
    id: 'top_bottom',
    name: 'Top + Bottom',
    category: 'promotional',
    emoji: '↕️',
    description: 'Headline at top, call-to-action at bottom',
    layers: [
      {
        text: 'New Arrival',
        xCentreFrac: 0.50, yCentreFrac: 0.10,
        widthFrac: 0.70, fontSizeFrac: 0.075,
        fontFamily: 'Playfair Display', fill: '#ffffff', fontStyle: 'bold italic',
        align: 'center', opacity: 1, rotation: 0,
        effect: 'none', effectParams: {},
      },
      {
        text: 'SHOP NOW →',
        xCentreFrac: 0.50, yCentreFrac: 0.90,
        widthFrac: 0.50, fontSizeFrac: 0.055,
        fontFamily: 'Montserrat', fill: '#ffffff', fontStyle: 'bold',
        align: 'center', opacity: 1, rotation: 0,
        effect: 'chrome_stroke',
        effectParams: { strokeColor: '#ffd700', strokeWidth: 2, shadowOffset: 2 },
      },
    ],
  },

  // ── 4. Price Badge ─────────────────────────────────────────────────────────
  {
    id: 'price_badge',
    name: 'Price Badge',
    category: 'promotional',
    emoji: '🏷️',
    description: 'Product name top-left, bold price bottom-right',
    layers: [
      {
        text: 'Product Name',
        xCentreFrac: 0.27, yCentreFrac: 0.10,
        widthFrac: 0.45, fontSizeFrac: 0.052,
        fontFamily: 'Open Sans', fill: '#ffffff', fontStyle: 'bold',
        align: 'left', opacity: 1, rotation: 0,
        effect: 'none', effectParams: {},
      },
      {
        text: '$49.99',
        xCentreFrac: 0.75, yCentreFrac: 0.87,
        widthFrac: 0.38, fontSizeFrac: 0.120,
        fontFamily: 'Impact', fill: '#ffd700', fontStyle: 'bold',
        align: 'center', opacity: 1, rotation: 0,
        effect: 'chrome_stroke',
        effectParams: { strokeColor: '#1a1a1a', strokeWidth: 3, shadowOffset: 3 },
      },
    ],
  },

  // ── 5. 3D Impact ───────────────────────────────────────────────────────────
  {
    id: '3d_impact',
    name: '3D Impact',
    category: 'bold',
    emoji: '💥',
    description: 'Large extruded headline — maximum visual impact',
    layers: [
      {
        text: 'BIG IMPACT',
        xCentreFrac: 0.50, yCentreFrac: 0.48,
        widthFrac: 0.88, fontSizeFrac: 0.125,
        fontFamily: 'Impact', fill: '#ffffff', fontStyle: 'bold',
        align: 'center', opacity: 1, rotation: 0,
        effect: 'extrude_3d',
        effectParams: { extrudeDepth: 8, extrudeColor: '#1a1a1a' },
      },
    ],
  },

  // ── 6. Gradient Headline ───────────────────────────────────────────────────
  {
    id: 'gradient_headline',
    name: 'Gradient Headline',
    category: 'bold',
    emoji: '🌈',
    description: 'Full-width gradient-fill title with subtitle',
    layers: [
      {
        text: 'GOLDEN OFFER',
        xCentreFrac: 0.50, yCentreFrac: 0.44,
        widthFrac: 0.88, fontSizeFrac: 0.115,
        fontFamily: 'Montserrat', fill: '#ffd700', fontStyle: 'bold',
        align: 'center', opacity: 1, rotation: 0,
        effect: 'gradient_fill',
        effectParams: { gradientTopColor: '#ffd700', gradientBottomColor: '#ff6b00' },
      },
      {
        text: 'Limited time only',
        xCentreFrac: 0.50, yCentreFrac: 0.58,
        widthFrac: 0.60, fontSizeFrac: 0.045,
        fontFamily: 'Open Sans', fill: '#ffffff', fontStyle: 'normal',
        align: 'center', opacity: 0.85, rotation: 0,
        effect: 'none', effectParams: {},
      },
    ],
  },

  // ── 7. Motion Brand ────────────────────────────────────────────────────────
  {
    id: 'motion_brand',
    name: 'Motion Brand',
    category: 'bold',
    emoji: '⚡',
    description: 'Speed-blur brand name for dynamic products',
    layers: [
      {
        text: 'BRAND NAME',
        xCentreFrac: 0.50, yCentreFrac: 0.50,
        widthFrac: 0.90, fontSizeFrac: 0.115,
        fontFamily: 'Impact', fill: '#ffffff', fontStyle: 'bold',
        align: 'center', opacity: 1, rotation: 0,
        effect: 'motion_blur',
        effectParams: { blurAmount: 18 },
      },
    ],
  },

  // ── 8. Minimal Elegant ─────────────────────────────────────────────────────
  {
    id: 'minimal_elegant',
    name: 'Minimal Elegant',
    category: 'elegant',
    emoji: '✨',
    description: 'Refined script headline with a small descriptor',
    layers: [
      {
        text: 'Elegance',
        xCentreFrac: 0.50, yCentreFrac: 0.45,
        widthFrac: 0.70, fontSizeFrac: 0.105,
        fontFamily: 'Playfair Display', fill: '#ffffff', fontStyle: 'italic',
        align: 'center', opacity: 1, rotation: 0,
        effect: 'none', effectParams: {},
      },
      {
        text: 'Discover the collection',
        xCentreFrac: 0.50, yCentreFrac: 0.57,
        widthFrac: 0.55, fontSizeFrac: 0.038,
        fontFamily: 'Open Sans', fill: '#ffffff', fontStyle: 'normal',
        align: 'center', opacity: 0.80, rotation: 0,
        effect: 'none', effectParams: {},
      },
    ],
  },

  // ── 9. Image Through Text ─────────────────────────────────────────────────
  {
    id: 'image_mask',
    name: 'Image Mask',
    category: 'elegant',
    emoji: '🖼️',
    description: 'Background image revealed through large text',
    layers: [
      {
        text: 'VISION',
        xCentreFrac: 0.50, yCentreFrac: 0.50,
        widthFrac: 0.92, fontSizeFrac: 0.170,
        fontFamily: 'Impact', fill: '#ffffff', fontStyle: 'bold',
        align: 'center', opacity: 1, rotation: 0,
        effect: 'image_through_text',
        effectParams: {},
      },
    ],
  },

  // ── 10. Lower Third ──────────────────────────────────────────────────────
  {
    id: 'lower_third',
    name: 'Lower Third',
    category: 'promotional',
    emoji: '📺',
    description: 'TV-style dark band at the bottom with title and subtitle',
    layers: [
      {
        text: 'PRODUCT NAME',
        xCentreFrac: 0.50, yCentreFrac: 0.80,
        widthFrac: 0.90, fontSizeFrac: 0.078,
        fontFamily: 'Montserrat', fill: '#ffffff', fontStyle: 'bold',
        align: 'center', opacity: 1, rotation: 0,
        effect: 'none', effectParams: {},
        bgShape: 'rect', bgColor: '#000000', bgOpacity: 0.70,
      },
      {
        text: 'Tagline or offer goes here',
        xCentreFrac: 0.50, yCentreFrac: 0.91,
        widthFrac: 0.88, fontSizeFrac: 0.040,
        fontFamily: 'Open Sans', fill: '#ffffffcc', fontStyle: 'normal',
        align: 'center', opacity: 0.90, rotation: 0,
        effect: 'none', effectParams: {},
        bgShape: 'rect', bgColor: '#000000', bgOpacity: 0.55,
      },
    ],
  },

  // ── 11. Pill Badge ────────────────────────────────────────────────────────
  {
    id: 'pill_badge',
    name: 'Pill Badge',
    category: 'promotional',
    emoji: '💊',
    description: 'Short text in a bold pill-shaped badge — great for offers',
    layers: [
      {
        text: '50% OFF',
        xCentreFrac: 0.50, yCentreFrac: 0.50,
        widthFrac: 0.46, fontSizeFrac: 0.090,
        fontFamily: 'Montserrat', fill: '#ffffff', fontStyle: 'bold',
        align: 'center', opacity: 1, rotation: 0,
        effect: 'none', effectParams: {},
        bgShape: 'pill', bgColor: '#e53e3e', bgOpacity: 0.92,
      },
    ],
  },

  // ── 12. Frosted Panel ────────────────────────────────────────────────────
  {
    id: 'frosted_panel',
    name: 'Frosted Panel',
    category: 'elegant',
    emoji: '❄️',
    description: 'Clean headline on a wide frosted glass panel',
    layers: [
      {
        text: 'YOUR HEADLINE',
        xCentreFrac: 0.50, yCentreFrac: 0.45,
        widthFrac: 0.88, fontSizeFrac: 0.085,
        fontFamily: 'Raleway', fill: '#ffffff', fontStyle: 'bold',
        align: 'center', opacity: 1, rotation: 0,
        effect: 'none', effectParams: {},
        bgShape: 'rect', bgColor: '#000000', bgOpacity: 0.45,
        letterSpacing: 3,
      },
      {
        text: 'Supporting copy or offer detail',
        xCentreFrac: 0.50, yCentreFrac: 0.57,
        widthFrac: 0.75, fontSizeFrac: 0.040,
        fontFamily: 'Open Sans', fill: '#ffffffdd', fontStyle: 'normal',
        align: 'center', opacity: 0.88, rotation: 0,
        effect: 'none', effectParams: {},
        bgShape: 'rect', bgColor: '#000000', bgOpacity: 0.40,
      },
    ],
  },

  // ── 13. E-commerce Stack ─────────────────────────────────────────────────
  {
    id: 'ecommerce_stack',
    name: 'E-commerce Stack',
    category: 'promotional',
    emoji: '🛍️',
    description: 'Product name · price · CTA — the classic product ad layout',
    layers: [
      {
        text: 'Product Name',
        xCentreFrac: 0.50, yCentreFrac: 0.72,
        widthFrac: 0.80, fontSizeFrac: 0.058,
        fontFamily: 'Montserrat', fill: '#ffffff', fontStyle: 'bold',
        align: 'center', opacity: 1, rotation: 0,
        effect: 'none', effectParams: {},
        bgShape: 'rect', bgColor: '#1a1a2e', bgOpacity: 0.75,
      },
      {
        text: '$49.99',
        xCentreFrac: 0.50, yCentreFrac: 0.82,
        widthFrac: 0.38, fontSizeFrac: 0.090,
        fontFamily: 'Oswald', fill: '#ffd700', fontStyle: 'bold',
        align: 'center', opacity: 1, rotation: 0,
        effect: 'none', effectParams: {},
      },
      {
        text: 'SHOP NOW →',
        xCentreFrac: 0.50, yCentreFrac: 0.92,
        widthFrac: 0.45, fontSizeFrac: 0.047,
        fontFamily: 'Montserrat', fill: '#1a1a2e', fontStyle: 'bold',
        align: 'center', opacity: 1, rotation: 0,
        effect: 'none', effectParams: {},
        bgShape: 'pill', bgColor: '#ffd700', bgOpacity: 1.0,
        letterSpacing: 2,
      },
    ],
  },

  // ── 14. Left Column ──────────────────────────────────────────────────────
  {
    id: 'left_column',
    name: 'Left Column',
    category: 'minimal',
    emoji: '◀',
    description: 'Left-aligned text stack — ideal for Stories and Reels',
    layers: [
      {
        text: 'BIG\nHEADLINE',
        xCentreFrac: 0.22, yCentreFrac: 0.40,
        widthFrac: 0.40, fontSizeFrac: 0.100,
        fontFamily: 'Oswald', fill: '#ffffff', fontStyle: 'bold',
        align: 'left', opacity: 1, rotation: 0,
        effect: 'none', effectParams: {},
        letterSpacing: 4,
      },
      {
        text: 'Short supporting\ncopy here',
        xCentreFrac: 0.22, yCentreFrac: 0.61,
        widthFrac: 0.38, fontSizeFrac: 0.042,
        fontFamily: 'Open Sans', fill: '#ffffffcc', fontStyle: 'normal',
        align: 'left', opacity: 0.85, rotation: 0,
        effect: 'none', effectParams: {},
      },
    ],
  },

  // ── 15. Corner Duo ───────────────────────────────────────────────────────
  {
    id: 'corner_duo',
    name: 'Corner Duo',
    category: 'minimal',
    emoji: '↗',
    description: 'Brand name top-right, tagline bottom-left — editorial style',
    layers: [
      {
        text: 'BRAND',
        xCentreFrac: 0.80, yCentreFrac: 0.08,
        widthFrac: 0.28, fontSizeFrac: 0.050,
        fontFamily: 'Teko', fill: '#ffffff', fontStyle: 'bold',
        align: 'right', opacity: 0.90, rotation: 0,
        effect: 'none', effectParams: {},
        letterSpacing: 5,
      },
      {
        text: 'Discover the\nnew collection',
        xCentreFrac: 0.18, yCentreFrac: 0.88,
        widthFrac: 0.32, fontSizeFrac: 0.042,
        fontFamily: 'Cormorant Garamond', fill: '#ffffff', fontStyle: 'italic',
        align: 'left', opacity: 0.85, rotation: 0,
        effect: 'none', effectParams: {},
      },
    ],
  },

  // ── 16. Split Band ───────────────────────────────────────────────────────
  {
    id: 'split_band',
    name: 'Split Band',
    category: 'bold',
    emoji: '🎞️',
    description: 'Full-width dark strip across the centre with a bold headline',
    layers: [
      {
        text: 'BOLD STATEMENT HERE',
        xCentreFrac: 0.50, yCentreFrac: 0.50,
        widthFrac: 0.96, fontSizeFrac: 0.085,
        fontFamily: 'Bebas Neue', fill: '#ffffff', fontStyle: 'normal',
        align: 'center', opacity: 1, rotation: 0,
        effect: 'none', effectParams: {},
        bgShape: 'rect', bgColor: '#000000', bgOpacity: 0.75,
        letterSpacing: 6,
      },
    ],
  },

  // ── 17. Diagonal Impact ──────────────────────────────────────────────────
  {
    id: 'diagonal_impact',
    name: 'Diagonal Impact',
    category: 'bold',
    emoji: '↗️',
    description: 'Slightly rotated bold text for energy and movement',
    layers: [
      {
        text: 'SALE',
        xCentreFrac: 0.50, yCentreFrac: 0.48,
        widthFrac: 0.90, fontSizeFrac: 0.180,
        fontFamily: 'Impact', fill: '#ffffff', fontStyle: 'bold',
        align: 'center', opacity: 1, rotation: -8,
        effect: 'chrome_stroke',
        effectParams: { strokeColor: '#ff0000', strokeWidth: 4, shadowOffset: 4 },
      },
    ],
  },

  // ── 18. Neon ─────────────────────────────────────────────────────────────
  {
    id: 'neon',
    name: 'Neon',
    category: 'bold',
    emoji: '🌃',
    description: 'Bright neon-style text — perfect for nightlife and tech brands',
    layers: [
      {
        text: 'NEON NIGHTS',
        xCentreFrac: 0.50, yCentreFrac: 0.48,
        widthFrac: 0.86, fontSizeFrac: 0.095,
        fontFamily: 'Raleway', fill: '#00ffcc', fontStyle: 'bold',
        align: 'center', opacity: 1, rotation: 0,
        effect: 'chrome_stroke',
        effectParams: { strokeColor: '#00ffcc', strokeWidth: 3, shadowOffset: 0 },
        letterSpacing: 8,
      },
      {
        text: 'open tonight',
        xCentreFrac: 0.50, yCentreFrac: 0.60,
        widthFrac: 0.50, fontSizeFrac: 0.042,
        fontFamily: 'Dancing Script', fill: '#ff00ff', fontStyle: 'normal',
        align: 'center', opacity: 0.90, rotation: 0,
        effect: 'none', effectParams: {},
      },
    ],
  },

  // ── 19. Script Quote ─────────────────────────────────────────────────────
  {
    id: 'script_quote',
    name: 'Script Quote',
    category: 'elegant',
    emoji: '✍️',
    description: 'Large script quote — luxury, lifestyle, and inspiration',
    layers: [
      {
        text: '"Live beautifully."',
        xCentreFrac: 0.50, yCentreFrac: 0.44,
        widthFrac: 0.82, fontSizeFrac: 0.095,
        fontFamily: 'Cormorant Garamond', fill: '#ffffff', fontStyle: 'italic',
        align: 'center', opacity: 0.95, rotation: 0,
        effect: 'none', effectParams: {},
      },
      {
        text: '— Brand Name',
        xCentreFrac: 0.50, yCentreFrac: 0.57,
        widthFrac: 0.45, fontSizeFrac: 0.038,
        fontFamily: 'Cormorant Garamond', fill: '#ffffffaa', fontStyle: 'normal',
        align: 'center', opacity: 0.75, rotation: 0,
        effect: 'none', effectParams: {},
        letterSpacing: 3,
      },
    ],
  },

  // ── 20. Breaking News ────────────────────────────────────────────────────
  {
    id: 'breaking_news',
    name: 'Breaking News',
    category: 'promotional',
    emoji: '📣',
    description: 'All-caps bold headline with a coloured accent underline',
    layers: [
      {
        text: 'ANNOUNCING',
        xCentreFrac: 0.50, yCentreFrac: 0.42,
        widthFrac: 0.85, fontSizeFrac: 0.055,
        fontFamily: 'Oswald', fill: '#ff3b3b', fontStyle: 'bold',
        align: 'center', opacity: 1, rotation: 0,
        effect: 'none', effectParams: {},
        letterSpacing: 10,
      },
      {
        text: 'THE BIG NEWS HEADLINE',
        xCentreFrac: 0.50, yCentreFrac: 0.53,
        widthFrac: 0.88, fontSizeFrac: 0.090,
        fontFamily: 'Montserrat', fill: '#ffffff', fontStyle: 'bold',
        align: 'center', opacity: 1, rotation: 0,
        effect: 'none', effectParams: {},
        letterSpacing: 1,
      },
      {
        text: '─────────────',
        xCentreFrac: 0.50, yCentreFrac: 0.63,
        widthFrac: 0.40, fontSizeFrac: 0.025,
        fontFamily: 'Montserrat', fill: '#ff3b3b', fontStyle: 'normal',
        align: 'center', opacity: 0.90, rotation: 0,
        effect: 'none', effectParams: {},
      },
    ],
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Matches the TextLayer interface in editorShared.tsx */
export interface ResolvedLayer {
  id: string; text: string; x: number; y: number; width: number;
  fontSize: number; fontFamily: string; fill: string; fontStyle: string;
  align: 'left' | 'center' | 'right'; opacity: number; rotation: number;
  effect: EffectId; effectParams: EffectParams;
  letterSpacing: number; lineHeight: number;
  bgShape: 'none' | 'rect' | 'pill'; bgColor: string; bgOpacity: number; bgPadding: number;
}

export function templateLayerToTextLayer(
  tl: TemplateLayer,
  stageWidth: number,
  stageHeight: number,
  nextId: () => string,
): ResolvedLayer {
  const width = tl.widthFrac * stageWidth;
  const fontSize = Math.max(10, Math.round(tl.fontSizeFrac * stageHeight));
  // Centre the box: x = centre_x - width/2
  const x = Math.max(0, tl.xCentreFrac * stageWidth - width / 2);
  // y = centre_y - half a line height (approximate)
  const y = Math.max(0, tl.yCentreFrac * stageHeight - fontSize * 0.65);

  return {
    id: nextId(),
    text: tl.text,
    x,
    y,
    width: Math.min(stageWidth, width),
    fontSize,
    fontFamily: tl.fontFamily,
    fill: tl.fill,
    fontStyle: tl.fontStyle,
    align: tl.align,
    opacity: tl.opacity,
    rotation: tl.rotation,
    effect: tl.effect,
    effectParams: { ...DEFAULT_EFFECT_PARAMS, ...tl.effectParams },
    letterSpacing: tl.letterSpacing ?? 0,
    lineHeight: 1.3,
    bgShape: tl.bgShape ?? 'none',
    bgColor: tl.bgColor ?? '#000000',
    bgOpacity: tl.bgOpacity ?? 0.6,
    bgPadding: 12,
  };
}
